import { Link } from 'react-router-dom';

interface PlanFeature {
  text: string;
  included: boolean;
}

interface PlanCardProps {
  name: string;
  nameEn: string;
  price: number;
  priceNote?: string;
  subNote?: string;
  period: string;
  badge?: string;
  badgeColor?: string;
  features: PlanFeature[];
  highlight: boolean;
  icon: string;
  iconBg: string;
  gradient: string;
  borderColor: string;
  onSelect: () => void;
  ctaLabel?: string;
  ctaIcon?: string;
}

export default function PlanCard({
  name, nameEn, price, priceNote, subNote, period, badge, badgeColor, features,
  highlight, icon, iconBg, gradient, borderColor, onSelect, ctaLabel, ctaIcon,
}: PlanCardProps) {
  return (
    <div
      className={`relative rounded-2xl border-2 overflow-hidden transition-all duration-300 hover:-translate-y-1 ${borderColor} ${highlight ? 'shadow-xl' : ''}`}
    >
      {badge && (
        <div className={`absolute top-4 right-4 text-xs font-bold px-3 py-1 rounded-full ${badgeColor}`}>
          {badge}
        </div>
      )}

      {/* Header */}
      <div className={`bg-gradient-to-br ${gradient} p-6 pb-8`}>
        <div className={`w-14 h-14 flex items-center justify-center ${iconBg} rounded-2xl mb-4`}>
          <i className={`${icon} text-2xl text-white`}></i>
        </div>
        <div className="text-xs font-semibold text-white/70 tracking-widest mb-1">{nameEn}</div>
        <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: "'Noto Serif SC', serif" }}>{name}</h3>
        {priceNote ? (
          <div>
            <div className="text-3xl font-bold text-white leading-none">{priceNote}</div>
            {subNote && <div className="text-white/60 text-xs mt-1.5">{subNote}</div>}
          </div>
        ) : (
          <div className="flex items-end gap-1">
            <span className="text-white/70 text-sm">¥</span>
            <span className="text-5xl font-bold text-white leading-none">{price}</span>
            <span className="text-white/70 text-sm mb-1">/{period}</span>
          </div>
        )}
        <div className="text-white/50 text-xs mt-1">按月计费 · 随时取消</div>
      </div>

      {/* Features */}
      <div className="bg-white p-6">
        <ul className="flex flex-col gap-3 mb-6">
          {features.map((f, i) => (
            <li key={i} className={`flex items-start gap-2.5 text-sm ${f.included ? 'text-gray-700' : 'text-gray-300'}`}>
              <div className={`w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 ${f.included ? 'bg-green-100' : 'bg-gray-100'}`}>
                <i className={`text-xs ${f.included ? 'ri-check-line text-green-600' : 'ri-close-line text-gray-400'}`}></i>
              </div>
              <span className={f.included ? '' : 'line-through'}>{f.text}</span>
            </li>
          ))}
        </ul>

        <button
          onClick={onSelect}
          className={`w-full py-3 rounded-full font-bold text-sm transition-all cursor-pointer whitespace-nowrap ${
            highlight
              ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white hover:from-amber-600 hover:to-yellow-600'
              : gradient.includes('emerald')
              ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600'
              : 'bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800'
          }`}
        >
          {ctaIcon && <i className={`${ctaIcon} mr-1`}></i>}
          {ctaLabel ?? (highlight ? '立即升级黄金会员' : '立即开通')}
        </button>

        <p className="text-center text-xs text-gray-400 mt-3">
          安全支付 · 随时取消 · 无隐藏费用
        </p>
      </div>
    </div>
  );
}
